import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  footerJoinLinks,
  footerMenu,
  footerMenuLinks,
  footerProjectLinks,
  menu,
  type MenuItem,
} from './siteMenu'

export function SiteLink({
  children,
  className,
  href,
  onClick,
}: {
  children: ReactNode
  className?: string
  href: string
  onClick?: () => void
}) {
  if (href.startsWith('/')) {
    return (
      <Link className={className} onClick={onClick} to={href}>
        {children}
      </Link>
    )
  }

  return (
    <a className={className} href={href} onClick={onClick}>
      {children}
    </a>
  )
}

function isActiveItem(item: MenuItem, pathname: string) {
  const path = item.href.split('#')[0]
  if (!path.startsWith('/')) return false
  if (path === '/') return pathname === '/'
  return pathname === path || pathname.startsWith(`${path}/`)
}

export function DesktopNav() {
  const { pathname } = useLocation()

  return (
    <nav aria-label="Primary navigation" className="hidden lg:flex lg:items-center lg:justify-start">
      <ul className="flex flex-wrap items-center justify-start gap-y-1 text-[12px] font-semibold uppercase leading-none text-white">
        {menu.map((item) => (
          <li className="group relative" key={item.label}>
            <SiteLink
              className={`block border-l border-white/45 px-3 py-2 transition-colors hover:text-aan-bright ${
                isActiveItem(item, pathname) ? 'text-aan-bright' : ''
              }`}
              href={item.href}
            >
              {item.label}
            </SiteLink>
            {item.children ? (
              <ul className="invisible absolute left-0 top-full z-30 min-w-48 border border-neutral-200 bg-white py-1 text-[12px] text-neutral-800 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100">
                {item.children.map((child) => (
                  <li key={child.label}>
                    <SiteLink className="block px-4 py-3 hover:bg-aan-bright hover:text-white" href={child.href}>
                      {child.label}
                    </SiteLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export function MobileNav() {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative z-40 lg:hidden">
      <button
        aria-expanded={open}
        aria-label="Open navigation menu"
        className="flex h-8 w-8 flex-col items-center justify-center gap-1 border border-white/40 bg-black/50"
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <span className="h-0.5 w-5 bg-white" />
        <span className="h-0.5 w-5 bg-white" />
        <span className="h-0.5 w-5 bg-white" />
      </button>
      {open ? (
        <div className="absolute right-0 top-10 w-64 border border-white/15 bg-black text-left shadow-2xl">
          {menu.map((item) => (
            <details className="border-b border-white/10" key={item.label}>
              <summary className="cursor-pointer list-none px-4 py-3 text-xs font-semibold uppercase text-white">
                <SiteLink href={item.href} onClick={() => setOpen(false)}>
                  {item.label}
                </SiteLink>
              </summary>
              {item.children ? (
                <div className="bg-white py-1 text-neutral-900">
                  {item.children.map((child) => (
                    <SiteLink
                      className="block px-5 py-2 text-xs font-medium hover:bg-aan-bright hover:text-white"
                      href={child.href}
                      key={child.label}
                      onClick={() => setOpen(false)}
                    >
                      {child.label}
                    </SiteLink>
                  ))}
                </div>
              ) : null}
            </details>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Compact black header used on every page other than the home page. */
export function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-black/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-8">
        <Link to="/" aria-label="African Art Network home">
          <img className="w-24 sm:w-32 lg:w-40" src="/assets/logo-white.png" alt="African Art Network" />
        </Link>
        <div className="hidden lg:block">
          <DesktopNav />
        </div>
        <MobileNav />
      </div>
    </header>
  )
}

export function SectionHeading({ children }: { children: string }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center text-center">
      <h2 className="font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">
        {children}
      </h2>
      <div className="mt-3 flex w-[290px] items-center justify-center sm:w-[480px]">
        <span className="h-1 flex-1 bg-aan-green" />
        <span className="mx-1 h-3 w-3 rounded-full bg-aan-green" />
        <span className="h-1 flex-1 bg-aan-green" />
      </div>
    </div>
  )
}

export function SiteFooter() {
  return (
    <footer className="bg-aan-footer text-white">
      <div className="mx-auto grid max-w-[1130px] gap-10 px-8 py-16 md:grid-cols-[1.2fr_1fr_1.25fr_1.45fr] md:gap-8 lg:py-20">
        <div className="text-center md:text-left">
          <img className="mx-auto w-52 md:mx-0" src="/assets/logo-white.png" alt="African Art Network" />
          <p className="mt-3 font-display text-[12px] font-semibold uppercase tracking-normal text-white">
            Promoting the African Experiences
          </p>
        </div>
        <div className="border-white/60 md:border-r md:pr-8">
          <h3 className="font-display text-xl font-semibold text-aan-bright">Website Menu</h3>
          <ul className="mt-3 space-y-1 text-[12px] uppercase leading-tight text-white">
            {footerMenu.map((item) => (
              <li key={item}>
                <SiteLink className="hover:text-aan-bright" href={footerMenuLinks[item]}>
                  {item}
                </SiteLink>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-justify text-[12px] leading-relaxed text-white/90">
            African Art Network is a movement dedicated to supporting and contributing to a vibrant creative economy by
            promoting the African experience and all forms of artistic and cultural expressions through various channels
            and platforms.
          </p>
        </div>
        <div className="border-white/60 md:border-r md:pr-8">
          <h3 className="font-display text-xl font-semibold text-aan-bright">Projects and Initiatives</h3>
          <ul className="mt-3 space-y-1 text-[12px] uppercase leading-tight text-white">
            {footerProjectLinks.map((item) => (
              <li key={item.label}>
                <SiteLink className="hover:text-aan-bright" href={item.href}>
                  {item.label}
                </SiteLink>
              </li>
            ))}
          </ul>
          <h3 className="mt-7 font-display text-xl font-semibold text-aan-bright">Vision:</h3>
          <p className="mt-2 text-justify text-[12px] leading-relaxed text-white/90">
            African Art Network aims to be the leading catalyst for the promotion African arts and culture foreseeing a
            thriving environment where artists can express their creativity, connect with audiences, and inspire the
            world.
          </p>
        </div>
        <div>
          <ul className="space-y-2 font-display text-[15px] font-semibold leading-tight text-aan-bright">
            {footerJoinLinks.map((item) => (
              <li key={item.label}>
                <SiteLink className="hover:text-white" href={item.href}>
                  {item.label}
                </SiteLink>
              </li>
            ))}
          </ul>
          <h3 className="mt-8 font-display text-xl font-semibold text-aan-bright">Mission:</h3>
          <p className="mt-2 text-justify text-[12px] leading-relaxed text-white/90">
            African Art Network aims to be the leading catalyst for the promotion African arts and culture foreseeing a
            thriving environment where artists can express their creativity, connect with audiences, and inspire the
            world.
          </p>
        </div>
      </div>
      <div className="bg-aan-footer px-6 pb-10 text-center text-[12px] text-white">
        THIS WEBSITE IS OWNED BY AFRICAN ART NETWORK LBG
      </div>
    </footer>
  )
}
